import VueCookies from 'vue-cookies'
import {
  getAccessToken as utilGetAccessToken,
  getEntityId as utilGetEntityId,
  getRefreshToken as utilGetRefreshToken,
  getUserId as utilGetUserId,
  logout as utilLogout,
  removeRegistrationTokens as utilRemoveRegistrationTokens,
  setAccessToken as utilSetAccessToken,
  setAuthRefreshToken as utilSetAuthRefreshToken,
  setAuthRefreshTokenExpirationDate as utilSetAuthRefreshExpiration,
  setAuthUserId as utilSetAuthUserId,
  setEntityId as utilSetEntityId,
} from '@/helpers/utils'

const COOKIE_TTL_SECONDS = 60 * 60 * 24

export type RegistrationStage = 1 | 2 | 4 | 5 | 6

/**
 * Cookie naming for registration stage tokens. Backend expects these exact
 * names — do NOT rename without coordinating with the API team.
 *
 * Stages 3 and 7 deliberately reuse stage 2 / 4 cookies respectively in the
 * backend's flow, so they are not direct keys here.
 */
const STAGE_COOKIE: Record<RegistrationStage, string> = {
  1: 'RegistrationToken',
  2: 'Stage2RegistrationToken',
  4: 'Stage3RegistrationToken',
  5: 'Stage3RegistrationToken',
  6: 'Stage4RegistrationToken',
}

/**
 * Single facade over auth token storage. Components and stores should depend
 * on this service rather than poking at cookies / localStorage / sessionStorage
 * directly.
 *
 * The underlying storage mechanisms (cookies for refresh / stage tokens,
 * sessionStorage for permissions, localStorage for `step3` form draft) are
 * unchanged from the legacy `helpers/utils.ts` accessors — this only adds a
 * typed, discoverable surface.
 */
export const TokenService = {
  // ─── Access / refresh ─────────────────────────────────────────────────────
  getAccessToken(): string | null {
    return (utilGetAccessToken() as string | null) ?? null
  },
  setAccessToken(token: string): void {
    utilSetAccessToken(token)
  },
  getRefreshToken(): string | null {
    return (utilGetRefreshToken() as string | null) ?? null
  },
  setRefreshToken(token: string, expiration?: string): void {
    utilSetAuthRefreshToken(token, expiration)
    if (expiration) utilSetAuthRefreshExpiration(expiration)
  },

  // ─── User / entity identifiers ────────────────────────────────────────────
  getUserId(): string | null {
    return (utilGetUserId() as string | null) ?? null
  },
  setUserId(id: string): void {
    utilSetAuthUserId(id)
  },
  getEntityId(): string | null {
    return (utilGetEntityId() as string | null) ?? null
  },
  setEntityId(id: string): void {
    utilSetEntityId(id)
  },

  // ─── Registration stage tokens ────────────────────────────────────────────
  /**
   * Retrieve a registration token. With no `stage` argument the legacy
   * fallback chain is used (access → Stage2 → Stage1) which matches what
   * `helpers/utils.getRegistrationToken()` returned and what request
   * interceptors / API services expect.
   */
  getRegistrationToken(stage?: RegistrationStage): string | null {
    if (stage) {
      return ((VueCookies as unknown) as { get: (k: string) => string | null })
        .get(STAGE_COOKIE[stage]) ?? null
    }
    const access = utilGetAccessToken() as string | null
    if (access) return access
    const cookies = (VueCookies as unknown) as { get: (k: string) => string | null }
    return cookies.get('Stage2RegistrationToken') ?? cookies.get('RegistrationToken')
  },

  setRegistrationToken(token: string, stage: RegistrationStage = 1): void {
    const cookieName = STAGE_COOKIE[stage]
    if (!cookieName) return
    ;((VueCookies as unknown) as {
      set: (k: string, v: string, ttl: number) => void
    }).set(cookieName, token, COOKIE_TTL_SECONDS)
  },

  /**
   * Highest registration step the user can resume to, derived purely from
   * which stage cookies are present. `goToStep(highestValid + 1)` is allowed.
   */
  getHighestValidRegistrationStep(): number {
    const cookies = (VueCookies as unknown) as { get: (k: string) => string | null }
    if (cookies.get('Stage4RegistrationToken')) return 7
    if (cookies.get('Stage3RegistrationToken')) return 6
    if (cookies.get('Stage2RegistrationToken')) return 5
    if (cookies.get('RegistrationToken')) return 3
    return 1
  },

  // ─── Permissions (sessionStorage) ─────────────────────────────────────────
  getPermissions<T = unknown>(): T | null {
    try {
      const stored = sessionStorage.getItem('user_permissions')
      return stored ? (JSON.parse(stored) as T) : null
    } catch {
      return null
    }
  },
  setPermissions(permissions: unknown): void {
    sessionStorage.setItem('user_permissions', JSON.stringify(permissions))
  },
  clearPermissions(): void {
    sessionStorage.removeItem('user_permissions')
  },

  // ─── Step 3 form draft (localStorage) ─────────────────────────────────────
  getStep3Data<T extends Record<string, unknown> = Record<string, unknown>>(): T | null {
    try {
      const saved = localStorage.getItem('registration_step3_data')
      return saved ? (JSON.parse(saved) as T) : null
    } catch {
      return null
    }
  },
  setStep3Data(data: Record<string, unknown>): void {
    localStorage.setItem('registration_step3_data', JSON.stringify(data))
  },
  clearStep3Data(): void {
    localStorage.removeItem('registration_step3_data')
  },

  // ─── Bulk clears ──────────────────────────────────────────────────────────
  clearRegistrationTokens(): void {
    utilRemoveRegistrationTokens()
  },
  logout(): void {
    utilLogout()
  },
}

export type ITokenService = typeof TokenService
